/**
 * The application's central people cache, shared through
 * `useModel('influencerContext')`.
 *
 * By far the most depended-on model - roughly 40 call sites - because almost
 * every surface renders people: thread answers, recommender rails, the sidebar,
 * search, the curation queues. It owns three things:
 *
 * 1. A module-scope `influencersMap` cache (see below) plus a serialising queue,
 *    so that N components asking for overlapping ids produce one batched request
 *    rather than N.
 * 2. Follow state (`followingIds` / `followingList`), kept in React state because
 *    the sidebar re-renders on every change.
 * 3. The "recently viewed" rail, persisted as ids in localStorage.
 *
 * Everything else here is a thin, uncached pass-through to `services/rest/api`,
 * following the model-wide convention: `success`/`failed` callbacks instead of
 * return values, and `skipErrorHandler: true` so screens render their own error
 * states.
 *
 * Because there is one shared instance, state changes here are global - a follow
 * toggled on the detail page is immediately reflected in the sidebar.
 */
import { useRef, useState } from 'react';
import {
  getV2InfluencerById,
  getV2InfluencersByIds,
  getV2InfluencerIdsByStatus,
  countV2InfluencersByStatus,
  getV2InfluencerCloneIds,
  getV2InfluencerNetwork,
  markInfluencerHomePageValidity,
  markInfluencerWikiPageValidity,
  markInfluencerSocialPageValidity,
  markInfluencerAliasNameValidity,
  searchInfluencers,
  getFollowingInfluencerIds,
  followInfluencer,
  unfollowInfluencer,
  autocompleteInfluencers,
  getBestRecommenders,
  getCandidatesByInfluencerId,
  getSimilarities,
  getCandidatesByIds,
  getMentionedEntites,
  getLatestFollowUps,
  getReferenceEntitesByInfluencerId,
  getBestRecommenderIdsByCandidateId,
  updateInfluencerStatus,
  getLatestInfluencers,
} from '@/services/rest/api';

/**
 * People cache, keyed by id.
 *
 * Deliberately at module scope rather than in a ref: it has to outlive the hook,
 * so navigating away from a screen and back does not re-fetch everyone. Nothing
 * evicts from it - the working set over one session is small enough that the
 * memory is not worth the bookkeeping, and staleness is bounded by page reload.
 */
const influencersMap = new Map<string, DATA.V2Influencer>();

/** localStorage key holding the ids of recently viewed people, newest first. */
const RECENTS_NAME = 'recents';

export default () => {
  const [followingIds, setFollowingIds] = useState<string[]>();

  const [recentList, setRecentList] = useState<DATA.V2Influencer[]>();
  const [followingList, setFollowingList] = useState<DATA.V2Influencer[]>();

  // Serialising queue shared by every id-based lookup. Without it, a page that
  // mounts a dozen cards at once would fire a dozen overlapping batch requests
  // for overlapping ids; chaining means each one sees what the previous one
  // already put in the cache.
  const queueRef = useRef(Promise.resolve());

  /**
   * The workhorse: resolve ids to people, hitting the network only for what the
   * cache is missing.
   *
   * @param isCached pass `false` to read through without polluting the cache -
   *   used where a caller needs a guaranteed-fresh copy.
   */
  const doGetInfluencersByIds = (
    ids: string[],
    success: (influencers: DATA.V2Influencer[]) => void,
    failed: () => void,
    isCached = true,
  ) => {
    queueRef.current = queueRef.current
      .then(async () => {
        const resultMap = new Map<string, DATA.V2Influencer>();
        const notExistedIds: string[] = [];

        // Split the request into cache hits and misses.
        ids.forEach((id) => {
          const influencer = influencersMap.get(id);
          if (influencer) {
            resultMap.set(id, influencer);
          } else {
            notExistedIds.push(id);
          }
        });

        try {
          if (notExistedIds.length > 0) {
            // The batch endpoint takes its ids in the URL path, so the request
            // has to be chunked to stay under the server's URL length limit.
            const batchSize = 20;
            for (let i = 0; i < notExistedIds.length; i += batchSize) {
              const batchIds = notExistedIds.slice(i, i + batchSize);
              const msg = await getV2InfluencersByIds(batchIds, { skipErrorHandler: true });
              if (msg.success && msg.data) {
                msg.data.forEach((influencer) => {
                  resultMap.set(influencer.id, influencer);
                  if (isCached) {
                    influencersMap.set(influencer.id, influencer);
                  }
                });
              } else {
                // Abandon the remaining chunks: a partial list would be
                // indistinguishable from a complete one to the caller.
                failed();
                return;
              }
            }
          }

          // Re-project onto the caller's id order - the batches came back in
          // whatever order the server chose - and drop ids that resolved to
          // nothing (deleted or merged records).
          const influencers = ids
            .map((id) => resultMap.get(id))
            .filter(Boolean) as DATA.V2Influencer[];

          success(influencers);
        } catch (error) {
          console.error('Error fetching influencers:', error);
          failed();
        }
      })
      .catch((err) => {
        // Keep the queue tail resolved; a rejection here would break every
        // subsequent lookup that chains onto it.
        console.error('Queue task error:', err);
        failed();
      });

    return queueRef.current;
  };

  // DEAD CODE - superseded by the queued `doGetInfluencersByIds` above and not
  // exported from the model. Kept only as a reference for the un-serialised
  // behaviour; safe to delete.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const oldDoGetInfluencersByIds = async (
    ids: string[],
    success: (influencers: DATA.V2Influencer[]) => void,
    failed: () => void,
    isCached = true,
  ) => {
    const resultMap = new Map<string, DATA.V2Influencer>();
    const notExistedIds: string[] = [];

    // 先从缓存拿
    ids.forEach((id) => {
      const influencer = influencersMap.get(id);
      if (influencer) {
        resultMap.set(id, influencer);
      } else {
        notExistedIds.push(id);
      }
    });

    try {
      if (notExistedIds.length > 0) {
        const batchSize = 20;
        for (let i = 0; i < notExistedIds.length; i += batchSize) {
          const batchIds = notExistedIds.slice(i, i + batchSize);
          const msg = await getV2InfluencersByIds(batchIds, { skipErrorHandler: true });
          if (msg.success && msg.data) {
            msg.data.forEach((influencer) => {
              resultMap.set(influencer.id, influencer);
              if (isCached) {
                influencersMap.set(influencer.id, influencer);
              }
            });
          } else {
            failed();
            return; // 遇到失败直接返回
          }
        }
      }

      // 保证顺序 + 过滤 null
      const influencers = ids
        .map((id) => {
          // console.log(id);
          return resultMap.get(id);
        })
        .filter(Boolean) as DATA.V2Influencer[];
      success(influencers);
    } catch (error) {
      console.error('Error fetching influencers:', error);
      failed();
    }
  };

  /**
   * Fetch one person. Always goes to the network - this is the detail view's
   * loader, and it refreshes the cache rather than reading from it.
   */
  const doGetInfluencerById = async (
    id: string,
    success: (influencer: DATA.V2Influencer) => void,
    failed: () => void,
  ) => {
    try {
      const msg = await getV2InfluencerById(id, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        const influencer = msg.data as DATA.V2Influencer;
        influencersMap.set(influencer.id, influencer);
        success(msg.data as DATA.V2Influencer);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error fetching influencer:', error);
      failed();
    }
  };

  /** One page of ids in a verification status. `lastId` is the cursor. */
  const doGetInfluencerIdsByStatus = async (
    vStatus: string,
    success: (ids: string[]) => void,
    failed: () => void,
    lastId?: string,
  ) => {
    try {
      const msg = await getV2InfluencerIdsByStatus(vStatus, lastId, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        const ids = msg.data as string[];
        success(ids);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error:', error);
      failed();
    }
  };

  /** Per-status totals, for the curation queue's tab badges. */
  const doCountInfluencersByStatus = async (
    success: (
      ids: {
        vStatus: string;
        count: number;
      }[],
    ) => void,
    failed: () => void,
  ) => {
    try {
      const msg = await countV2InfluencersByStatus({ skipErrorHandler: true });
      if (msg.success && msg.data) {
        const infos = msg.data as { vStatus: string; count: number }[];
        success(infos);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error:', error);
      failed();
    }
  };

  /** Records suspected of being duplicates of this one. */
  const doGetPotentialInfluencerCloneIds = async (
    id: string,
    success: (obj: { originalIds: string[]; originalCloneIds: string[] }) => void,
    failed: () => void,
  ) => {
    try {
      const msg = await getV2InfluencerCloneIds(id, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        const infos = msg.data as { originalIds: string[]; originalCloneIds: string[] };
        success(infos);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error:', error);
      failed();
    }
  };

  /** Everything linked to a person - aliases and reference pages - in one call. */
  const doGetInfluencerNetwork = async (
    id: string,
    success: (obj: {
      influencerIds: string[];
      aliasNames?: DATA.AliasName[];
      homePage?: DATA.HomePage;
      wikiPage?: DATA.WikiPage;
      socialPages?: DATA.SocialPage[];
    }) => void,
    failed: () => void,
  ) => {
    try {
      const msg = await getV2InfluencerNetwork(id, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        const infos = msg.data as {
          influencerIds: string[];
          aliasNames?: DATA.AliasName[];
          homePage?: DATA.HomePage;
          wikiPage?: DATA.WikiPage;
          socialPages?: DATA.SocialPage[];
        };
        success(infos);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error:', error);
      failed();
    }
  };

  // The four `doMarkInfluencer*` calls below judge the *link* between a person
  // and a reference, not the reference itself; the same page can be valid for
  // one person and wrongly attributed to another.
  const doMarkInfluencerHomePageValidity = async (
    success: (obj: DATA.InfluencerHomePage) => void,
    failed: () => void,
    influencerId: string,
    homePageId: string,
    isInvalid: boolean,
  ) => {
    try {
      const msg = await markInfluencerHomePageValidity(influencerId, homePageId, isInvalid, {
        skipErrorHandler: true,
      });
      if (msg.success && msg.data) {
        success(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error:', error);
      failed();
    }
  };

  const doMarkInfluencerWikiPageValidity = async (
    success: (obj: DATA.InfluencerWikiPage) => void,
    failed: () => void,
    influencerId: string,
    homePageId: string,
    isInvalid: boolean,
  ) => {
    try {
      const msg = await markInfluencerWikiPageValidity(influencerId, homePageId, isInvalid, {
        skipErrorHandler: true,
      });
      if (msg.success && msg.data) {
        success(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error:', error);
      failed();
    }
  };

  const doMarkInfluencerSocialPageValidity = async (
    success: (obj: DATA.InfluencerSocialPage) => void,
    failed: () => void,
    influencerId: string,
    homePageId: string,
    isInvalid: boolean,
  ) => {
    try {
      const msg = await markInfluencerSocialPageValidity(influencerId, homePageId, isInvalid, {
        skipErrorHandler: true,
      });
      if (msg.success && msg.data) {
        success(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error:', error);
      failed();
    }
  };

  const doMarkInfluencerAliasNameValidity = async (
    success: (obj: DATA.InfluencerAliasName) => void,
    failed: () => void,
    influencerId: string,
    homePageId: string,
    isInvalid: boolean,
  ) => {
    try {
      const msg = await markInfluencerAliasNameValidity(influencerId, homePageId, isInvalid, {
        skipErrorHandler: true,
      });
      if (msg.success && msg.data) {
        success(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error:', error);
      failed();
    }
  };

  /** Typeahead lookup for the search box. (Name typo is load-bearing - callers use it.) */
  const doAutocomplteInfluencers = async (
    success: (obj: DATA.V2Influencer[]) => void,
    failed: () => void,
    term: string,
    limit: number = 10,
  ) => {
    try {
      const msg = await autocompleteInfluencers(term, limit, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        success(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error:', error);
      failed();
    }
  };

  /** People best placed to answer about a term, for the recommender rail. */
  const doGetBestRecommencers = async (
    success: (obj: DATA.V2Influencer[]) => void,
    failed: () => void,
    term: string,
    limit: number = 10,
  ) => {
    try {
      const msg = await getBestRecommenders(term, limit, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        success(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error:', error);
      failed();
    }
  };

  /** Full search over people - broader and slower than the autocomplete above. */
  const doSearchInfluencers = async (
    success: (obj: DATA.V2Influencer[]) => void,
    failed: () => void,
    term: string,
    limit: number = 100,
  ) => {
    try {
      const msg = await searchInfluencers(term, limit, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        console.log(msg.data);
        await doGetInfluencersByIds(
          msg.data,
          (influencers) => success(influencers),
          () => {},
          false,
        );
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error:', error);
      failed();
    }
  };

  /** People near this one in the embedding space, with their scores. */
  const doGetSimilarities = async (
    success: (obj: DATA.V2Influencer[]) => void,
    failed: () => void,
    influencerId: string,
  ) => {
    try {
      const msg = await getSimilarities(influencerId, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        await doGetInfluencersByIds(
          msg.data,
          (influencers) => success(influencers),
          () => {},
          false,
        );
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error:', error);
      failed();
    }
  };

  /**
   * Load just the ids the user follows. Cheap enough to run on mount so that
   * follow buttons can render their state without waiting for the full list.
   */
  const doLoadFollowingInfluencerIds = async () => {
    try {
      const msg = await getFollowingInfluencerIds({ skipErrorHandler: true });
      if (msg.success && msg.data) {
        setFollowingIds(msg.data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  /**
   * Follow a person and reflect it in both the id set and the hydrated list.
   *
   * Guarded on `followingIds` being loaded: acting on an unknown follow state
   * would let the UI drift out of sync with the server.
   */
  const doFollowInfluencer = async (influencer: DATA.V2Influencer) => {
    if (!followingIds) return;
    if (followingIds.includes(influencer.id)) return;
    try {
      const msg = await followInfluencer(influencer.id, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        setFollowingIds((ids) => {
          if (ids) {
            return [influencer.id, ...ids];
          }
          return [influencer.id];
        });
        setFollowingList((objs) => {
          if (objs) {
            // BUG: `lst` is sorted and then thrown away - the return value is a
            // fresh array built from the unsorted `objs`, so people always sort
            // ahead of organisations here only by luck of insertion order.
            // `doLoadFollowingInfluencers` below does the same sort correctly.
            const lst = [influencer, ...objs];
            lst.sort((a, b) => {
              if (a.entityType === 'PERSON' && b.entityType !== 'PERSON') {
                return -1; // people first
              } else if (a.entityType !== 'PERSON' && b.entityType === 'PERSON') {
                return 1;
              } else {
                return 0; // otherwise keep the existing relative order
              }
            });
            return [influencer, ...objs];
          }
          return [influencer];
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  /** Unfollow a person, dropping them from both the id set and the list. */
  const doUnfollowInfluencer = async (id: string) => {
    if (!followingIds) return;
    if (!followingIds.includes(id)) return;
    try {
      const msg = await unfollowInfluencer(id, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        setFollowingIds((ids) => {
          if (ids) {
            return ids.filter((v) => v !== id);
          }
          return ids;
        });
        setFollowingList((objs) => {
          if (objs) {
            return objs.filter((v) => v.id !== id);
          }
          return objs;
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  /**
   * Load the followed people in full: ids first, then hydrate them through the
   * cache. Ordered people-before-organisations for the sidebar.
   */
  const doLoadFollowingInfluencers = async () => {
    try {
      const msg = await getFollowingInfluencerIds({ skipErrorHandler: true });
      if (msg.success && msg.data) {
        setFollowingIds(msg.data);
        await doGetInfluencersByIds(
          msg.data,
          (influencers) => {
            influencers.sort((a, b) => {
              if (a.entityType === 'PERSON' && b.entityType !== 'PERSON') {
                return -1; // people first
              } else if (a.entityType !== 'PERSON' && b.entityType === 'PERSON') {
                return 1;
              } else {
                return 0; // otherwise keep the existing relative order
              }
            });

            setFollowingList(influencers);
          },
          () => {},
          true,
        );
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  /** Recommenders for an answer: ids from the API, then hydrated via the cache. */
  const doGetRecommendersByCandidateId = async (
    candidateId: string,
    onSuccess: (objs: DATA.V2Influencer[]) => void,
    failed: () => void,
  ) => {
    try {
      const msg = await getBestRecommenderIdsByCandidateId(candidateId, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        doGetInfluencersByIds(
          msg.data,
          (influencers) => {
            onSuccess(influencers);
          },
          () => {},
          true,
        );
      } else {
        failed();
      }
    } catch (error) {
      failed();
      console.error('Error:', error);
    }
  };

  /**
   * Rebuild the "recently viewed" rail from localStorage.
   *
   * Only ids are persisted, so the entries are re-hydrated here and then put
   * back into the stored order - the batch fetch does not preserve it.
   */
  const doLoadRecentInfluencers = async () => {
    const str = localStorage.getItem(RECENTS_NAME);
    const storedIds: string[] = str ? JSON.parse(str) : [];
    doGetInfluencersByIds(
      storedIds,
      (influencers) => {
        const sortedInfluencers = storedIds
          .map((id) => influencers.find((inf) => inf.id === id))
          .filter(Boolean) as DATA.V2Influencer[];
        setRecentList(sortedInfluencers);
      },
      () => {},
      true,
    );
  };

  /** Most recently added people, for the discovery rail. */
  const doGetLatestInfluencers = async (
    onSuccess: (objs: DATA.V2Influencer[]) => void,
    failed: () => void,
  ) => {
    try {
      const msg = await getLatestInfluencers({
        skipErrorHandler: true,
      });
      if (msg && msg.data) {
        onSuccess(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error doGetLatestInfluencers:', error);
      failed();
    } finally {
    }
  };

  /**
   * Record a visit: move the person to the front of the recents list, capped at
   * 20. Filtering first is what makes a repeat visit a move rather than a
   * duplicate entry.
   *
   * No-ops until `doLoadRecentInfluencers` has run, so that an early call
   * cannot overwrite the stored list with a single entry.
   */
  const doPushInfluencerToRecent = async (obj: DATA.V2Influencer) => {
    if (!recentList) {
      return;
    }
    const filteredInfluencers = recentList.filter((v) => v.id !== obj.id);
    const updatedInfluencers = [obj, ...filteredInfluencers].slice(0, 20);
    localStorage.setItem(RECENTS_NAME, JSON.stringify(updatedInfluencers.map((v) => v.id)));
    setRecentList(updatedInfluencers);
  };

  /** Answers attributed to a person, paged backwards from `before`. */
  const doGetCandidatesByInfluencerId = async (
    onSuccess: (objs: DATA.V2Candidate[]) => void,
    failed: () => void,
    influencerId: string,
    before?: Date,
  ) => {
    try {
      const msg = await getCandidatesByInfluencerId(influencerId, before, 10, {
        skipErrorHandler: true,
      });
      if (msg && msg.data) {
        const objs = msg.data.map((obj) => {
          const parsed = {
            ...obj,
            createdAt: new Date(obj.createdAt),
            updatedAt: new Date(obj.updatedAt),
          };
          return parsed;
        });
        onSuccess(objs);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error doGetCandidatesByInfluencerId:', error);
      failed();
    } finally {
    }
  };

  /** Entities this person is mentioned alongside. */
  const doGetMentionedEntitiesByInfluencerId = async (
    onSuccess: (objs: DATA.V2MentionedEntity[]) => void,
    failed: () => void,
    influencerId: string,
    before?: string,
  ) => {
    try {
      const msg = await getMentionedEntites(influencerId, before, 10, {
        skipErrorHandler: true,
      });
      if (msg && msg.data) {
        onSuccess(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error doGetMentionedEntitiesByInfluencerId:', error);
      failed();
    }
  };

  /** Sources backing what is known about this person. */
  const doGetReferenceEntitiesByInfluencerId = async (
    onSuccess: (objs: DATA.V2ReferenceEntity[]) => void,
    failed: () => void,
    influencerId: string,
    before?: string,
  ) => {
    try {
      const msg = await getReferenceEntitesByInfluencerId(influencerId, before, 10, {
        skipErrorHandler: true,
      });
      if (msg && msg.data) {
        onSuccess(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error doGetReferenceEntitiesByInfluencerId:', error);
      failed();
    }
  };

  /** Most recent follow-up questions raised about this person. */
  const doGetLatestFollowUpsByInfluencerId = async (
    onSuccess: (objs: string[]) => void,
    failed: () => void,
    influencerId?: string,
  ) => {
    try {
      const msg = await getLatestFollowUps(influencerId, {
        skipErrorHandler: true,
      });
      if (msg && msg.data) {
        onSuccess(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error doGetLatestFollowUpsByInfluencerId:', error);
      failed();
    }
  };

  /** Batch answer lookup. Unlike people, answers are not cached here. */
  const doGetCandidatesByIds = async (
    onSuccess: (objs: DATA.V2Candidate[]) => void,
    failed: () => void,
    ids: string[],
  ) => {
    try {
      const msg = await getCandidatesByIds(ids, {
        skipErrorHandler: true,
      });
      if (msg && msg.data) {
        const objs = msg.data.map((obj) => {
          const parsed = {
            ...obj,
            createdAt: new Date(obj.createdAt),
            updatedAt: new Date(obj.updatedAt),
          };
          return parsed;
        });
        onSuccess(objs);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error doGetCandidatesByIds:', error);
      failed();
    }
  };

  /** Move a record through the verification workflow (curation screens only). */
  const doUpdateInfluencerStatus = async (
    onSuccess: (obj: { influencerId: string; status: string }) => void,
    failed: () => void,
    id: string,
    status: string,
  ) => {
    try {
      const msg = await updateInfluencerStatus(id, status, {
        skipErrorHandler: true,
      });
      if (msg && msg.data) {
        onSuccess(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error doUpdateInfluencerStatus:', error);
      failed();
    }
  };

  return {
    followingIds,
    followingList,
    recentList,
    doGetInfluencersByIds,
    doGetInfluencerById,
    doGetInfluencerIdsByStatus,
    doCountInfluencersByStatus,
    doAutocomplteInfluencers,
    doGetBestRecommencers,
    doSearchInfluencers,
    doGetSimilarities,
    doFollowInfluencer,
    doUnfollowInfluencer,
    doGetPotentialInfluencerCloneIds,
    doGetInfluencerNetwork,
    doMarkInfluencerHomePageValidity,
    doMarkInfluencerWikiPageValidity,
    doMarkInfluencerSocialPageValidity,
    doMarkInfluencerAliasNameValidity,
    doLoadRecentInfluencers,
    doLoadFollowingInfluencers,
    doPushInfluencerToRecent,
    doLoadFollowingInfluencerIds,
    doGetCandidatesByInfluencerId,
    doGetMentionedEntitiesByInfluencerId,
    doGetReferenceEntitiesByInfluencerId,
    doGetLatestFollowUpsByInfluencerId,
    doGetCandidatesByIds,
    doGetRecommendersByCandidateId,
    doUpdateInfluencerStatus,
    doGetLatestInfluencers,
  };
};
